import { z } from "zod";
import { zId, zMoney, zQueryBoolean } from "./common.js";

// ---- moves management gaps (CT-RM-07) ----

/** A portfolio donor with no human touch in this many days is flagged "no touch". */
export const NO_TOUCH_DAYS = 90;

export const PORTFOLIO_STAGES = ["identification", "qualification", "cultivation", "solicitation", "stewardship"] as const;
export type PortfolioStage = (typeof PORTFOLIO_STAGES)[number];

/** Next open planned move (a task linked to the donor and owned by the assignment's officer). */
export interface PlannedMove {
  id: string;
  title: string;
  dueDate: string | null;
}

export interface PortfolioRow {
  id: string;
  constituentId: string;
  constituentName: string;
  lifetimeGiving: number;
  lastGiftDate: string | null;
  officerId: string;
  officerName: string | null;
  stage: PortfolioStage;
  askAmount: number | null;
  askDate: string | null;
  nextStep: string | null;
  stageHistory: unknown[];
  updatedAt: string;
  /** most recent human touch: activity, gift, or completed task (ISO timestamp) */
  lastTouchAt: string | null;
  /** whole days since lastTouchAt; null when never touched */
  daysSinceTouch: number | null;
  /** no touch in NO_TOUCH_DAYS+ days (or never) */
  noTouch: boolean;
  nextMove: PlannedMove | null;
  openMoves: number;
  overdueMoves: number;
}

/**
 * Fiscal years are labelled by the calendar year they END in (FY2027 with a July start =
 * 2026-07-01 … 2027-06-30). A January start makes FY == calendar year.
 */
export function fiscalYearOf(isoDate: string, startMonth: number): number {
  const year = Number(isoDate.slice(0, 4));
  const month = Number(isoDate.slice(5, 7));
  return startMonth === 1 || month < startMonth ? year : year + 1;
}

/** Inclusive start / exclusive end ISO dates for a fiscal year. */
export function fiscalYearRange(fiscalYear: number, startMonth: number): { start: string; end: string } {
  const startYear = startMonth === 1 ? fiscalYear : fiscalYear - 1;
  const mm = String(startMonth).padStart(2, "0");
  return { start: `${startYear}-${mm}-01`, end: `${startYear + 1}-${mm}-01` };
}

export const zGoalsQuery = z.object({
  fiscalYear: z.coerce.number().int().min(2000).max(2100).optional(),
  officerId: zId.optional(),
  mine: zQueryBoolean.optional(),
});

export const zUpsertOfficerGoal = z.object({
  officerId: zId,
  fiscalYear: z.number().int().min(2000).max(2100),
  /** dollars raised goal, integer cents */
  raisedGoal: zMoney.nullable(),
  asksGoal: z.number().int().min(0).max(100_000).nullable(),
  movesGoal: z.number().int().min(0).max(100_000).nullable(),
});
export type UpsertOfficerGoal = z.infer<typeof zUpsertOfficerGoal>;

export interface OfficerGoalMetrics {
  /** integer cents */
  raised: number;
  asks: number;
  moves: number;
}

export interface OfficerGoalRow {
  officerId: string;
  officerName: string | null;
  portfolioSize: number;
  goal: { raised: number | null; asks: number | null; moves: number | null } | null;
  progress: OfficerGoalMetrics;
}

export interface OfficerGoalsResult {
  fiscalYear: number;
  start: string;
  end: string;
  officers: OfficerGoalRow[];
}
